import { QueryClient } from '@tanstack/react-query'
import { isMaintenanceError, onIdentityChange } from '@ventawiz/web-kit'

import { errorType } from '@/lib/errors'

/**
 * The shared QueryClient and its defaults.
 *
 * Built by a factory rather than exported as a module singleton: every test
 * needs its own client, or one test's cache answers another test's query and
 * the suite passes in one order and fails in another.
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // A dashboard reads the same list on every navigation. Without a
        // stale time, React Query refetches on each mount and the UI flickers
        // through a loading state it did not need.
        staleTime: 30_000,
        // Retrying a 404 or a 422 just delays the error the user needs to
        // see. Only server faults and network failures are worth a retry.
        retry: (failureCount, error) => {
          // A maintenance 503 will not succeed before the window ends, and the
          // MaintenanceGate is already polling for that and will reload the
          // page. Retrying would only add load to a platform being worked on.
          if (isMaintenance(error)) return false
          const status = (error as { status?: number }).status
          if (status !== undefined && status >= 400 && status < 500) return false
          return failureCount < 2
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Never. A retried POST creates two rows.
        retry: false,
      },
    },
  })
}

/**
 * Whether a query failed because the platform is in maintenance.
 *
 * Two shapes: the kit's `MaintenanceError`, thrown by `fetchWithSession`, and
 * the raw backend envelope (`error.type === "maintenance"`) for a call that
 * reached the generated client some other way.
 */
export function isMaintenance(error: unknown): boolean {
  return isMaintenanceError(error) || errorType(error) === 'maintenance'
}

/**
 * Drop the whole cache when who is signed in, or where, changes.
 *
 * Everything cached was fetched for the previous principal, application or
 * tenant — a tenant switch (in this tab or another), a different person
 * signing in, a sign-out. `clear()` rather than `invalidateQueries()`:
 * invalidation refetches what is on screen and shows the old tenant's rows
 * while it does. A routine token refresh is not a change and clears nothing.
 *
 * `previous` is null on the first sign-in of a page load, when nothing was
 * cached for anybody; clearing then would only cancel the queries waiting for
 * that very session.
 *
 * @returns The unsubscribe function.
 */
export function clearOnIdentityChange(queryClient: QueryClient): () => void {
  return onIdentityChange(({ previous }) => {
    if (previous) queryClient.clear()
  })
}
